'use client'

import {Box, Text} from '@blocks'
import {
   ROUTE_HERO_BANNER_ASPECT_RATIO,
   ROUTE_HERO_BANNER_DESKTOP_MEDIA_QUERY,
   ROUTE_HERO_BANNER_HEADLINE,
   ROUTE_HERO_BANNER_MAX_HEIGHT_PX,
   ROUTE_HERO_BANNER_MIN_HEIGHT_MOBILE_PX,
   sitePrimaryHex
} from '@constants'
import {useMediaQuery} from '@hooks'
import {
   SITE_LAYOUT_HEADER_DESKTOP_MEDIA_QUERY,
   SITE_LAYOUT_HEADER_HEIGHT_DESKTOP_PX,
   SITE_LAYOUT_HEADER_HEIGHT_MOBILE_PX
} from '@utilities'
import Image from 'next/image'
import {RouteHeroHeadlineOverlay} from '@/blocks/site/RouteHeroHeadlineOverlay'

/**
 * Matches the homepage hero language but with larger bubbles for route heroes.
 */
const routeHeroBubblePattern = `url("data:image/svg+xml,${encodeURIComponent(
   '<svg width="88" height="88" viewBox="0 0 88 88" xmlns="http://www.w3.org/2000/svg"><g transform="translate(24,24)" fill="none" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"/><path d="M18 24a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/><path d="M12 10a6 6 0 1 0 12 0a6 6 0 1 0 -12 0"/></g></svg>'
)})"`

type Payload = Readonly<{
   headline: string
   imageSource?: string
   imageAlternativeText?: string
}>

export const RoutePageHero = ({
   headline,
   imageSource,
   imageAlternativeText
}: Payload) => {
   const headlineDesktop = useMediaQuery(
      ROUTE_HERO_BANNER_DESKTOP_MEDIA_QUERY
   )
   const isSiteLayoutDesktop = useMediaQuery(
      SITE_LAYOUT_HEADER_DESKTOP_MEDIA_QUERY
   )
   const siteLayoutHeaderHeightPx = isSiteLayoutDesktop
      ? SITE_LAYOUT_HEADER_HEIGHT_DESKTOP_PX
      : SITE_LAYOUT_HEADER_HEIGHT_MOBILE_PX

   return (
      <Box
         pos={'relative'}
         w={'100vw'}
         style={{
            aspectRatio: ROUTE_HERO_BANNER_ASPECT_RATIO,
            borderRadius: 0,
            marginLeft: 'calc(50% - 50vw)',
            marginRight: 'calc(50% - 50vw)',
            maxHeight: headlineDesktop
               ? ROUTE_HERO_BANNER_MAX_HEIGHT_PX.desktop
               : ROUTE_HERO_BANNER_MAX_HEIGHT_PX.mobile,
            minHeight: headlineDesktop
               ? undefined
               : ROUTE_HERO_BANNER_MIN_HEIGHT_MOBILE_PX,
            maxWidth: '100vw',
            overflow: 'hidden'
         }}
      >
         {imageSource ? (
            <Image
               fill
               fetchPriority={'high'}
               priority
               sizes={'100vw'}
               src={imageSource}
               alt={imageAlternativeText ?? ''}
               style={{
                  objectFit: 'cover',
                  objectPosition: 'center'
               }}
            />
         ) : (
            <Box
               aria-hidden
               style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: sitePrimaryHex
               }}
            />
         )}
         {!imageSource ? (
            <Box
               aria-hidden
               style={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.07,
                  backgroundImage: routeHeroBubblePattern,
                  pointerEvents: 'none',
                  userSelect: 'none'
               }}
            />
         ) : null}
         {imageSource ? (
            <Box
               aria-hidden
               style={{
                  position: 'absolute',
                  inset: 0,
                  background:
                     'linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.48))',
                  pointerEvents: 'none'
               }}
            />
         ) : null}
         <RouteHeroHeadlineOverlay
            headlineDesktop={headlineDesktop}
            siteLayoutHeaderHeightPx={siteLayoutHeaderHeightPx}
         >
            <Text
               c={ROUTE_HERO_BANNER_HEADLINE.color}
               fz={
                  headlineDesktop
                     ? ROUTE_HERO_BANNER_HEADLINE.fontSizePx.desktop
                     : ROUTE_HERO_BANNER_HEADLINE.fontSizePx.mobile
               }
               fw={ROUTE_HERO_BANNER_HEADLINE.fontWeight}
               lh={ROUTE_HERO_BANNER_HEADLINE.lineHeight}
               ta={'center'}
               maw={ROUTE_HERO_BANNER_HEADLINE.maxWidthPx}
            >
               {headline}
            </Text>
         </RouteHeroHeadlineOverlay>
      </Box>
   )
}
